/**
 * toastUi.tsx — Utilidades de presentación de toasts (limitar + ancho ajustado).
 *
 * QUÉ HACE:
 * - ToastLimit: limita los toasts visibles a 1 (descarta los más antiguos).
 * - ToastFitWidth: encoge cada toast al ancho real de su línea de texto más
 *   larga (tras el wrap), eliminando el espacio muerto a la derecha (toast
 *   "de corrido" con hueco innecesario después de la X). Mide el texto REAL ya
 *   renderizado del toast (no un probe), por lo que es inmune a:
 *     • zoom de texto / accesibilidad del sistema (común en tablets Android),
 *     • fuentes web que cargan tarde (swap tardío de Inter),
 *     • diferencias de layout del WebView nativo de Capacitor.
 *
 * QUÉ NO HACE: configurar el <Toaster/> global (eso sigue en main.tsx).
 *
 * Cómo ajusta el ancho: CSS no permite que un contenedor con max-width se
 * encoja al ancho de la línea más larga después de envolver. Cada pasada parte
 * del ancho natural (fit-content/max-width) y solo puede encoger hasta la
 * línea real más ancha + icono + X + gaps + padding. Se repite hasta converger
 * (1-3 pasadas), por lo que no puede quedarse ancho de más ni oscilar. Se
 * re-ejecuta cuando cambia el mensaje de un toast ya visible (mismo id), al
 * cargar las fuentes y al rotar/redimensionar.
 */
import { useEffect } from "react";
import { useToasterStore, toast } from "react-hot-toast";

// Limita el número de toasts visibles a 1 — descarta los más antiguos
export function ToastLimit() {
  const { toasts } = useToasterStore();
  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= 1)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);
  return null;
}

// Máximo de pasadas de ajuste por toast (lo normal es converger en 1-3).
const MAX_FIT_PASSES = 6;

/**
 * Ajusta el ancho del toast al ancho REAL de la línea más larga tras el wrap.
 *
 * En lugar de medir con un elemento "probe" (que replica tipografía CSS pero
 * no el zoom de texto del sistema ni el render del WebView nativo), mide las
 * líneas ya pintadas del propio toast con un Range sobre el mensaje y encoge
 * el contenedor a la línea más ancha. El ancho se aplica con !important vía
 * setProperty para ganar al `width: fit-content !important` de index.css.
 */
export function ToastFitWidth() {
  useEffect(() => {
    const pend = new Set<HTMLElement>();
    let rafId = 0;

    /** Escala X actual de la animación de entrada (matrix/matrix3d → scaleX). */
    const currentScale = (el: HTMLElement): number => {
      const transform = getComputedStyle(el).transform;
      if (!transform || transform === "none") return 1;
      const m =
        /matrix\((-?[\d.]+)/.exec(transform) ||
        /matrix3d\((-?[\d.]+)/.exec(transform);
      return m ? Math.max(Math.abs(parseFloat(m[1])), 1e-3) : 1;
    };

    /**
     * Ancho real de la línea de texto más larga del mensaje. getClientRects
     * devuelve geometría transformada (la entrada escala el toast 0.6 → 1),
     * por eso se divide entre la escala actual para obtener píxeles de layout.
     */
    const widestTextLine = (status: HTMLElement, scale: number): number => {
      const text = (status.textContent || "").trim();
      if (!text) return 0;
      const range = document.createRange();
      range.selectNodeContents(status);
      const rects = range.getClientRects();
      let widest = 0;
      for (let i = 0; i < rects.length; i++) {
        const width = rects[i].width / scale;
        if (width > widest) widest = width;
      }
      return widest;
    };

    /** Intenta encoger el toast; devuelve true si cambió el ancho aplicado. */
    const fitOnce = (toastEl: HTMLElement): boolean => {
      const status = toastEl.querySelector(
        'div[role="status"], div[role="alert"]',
      ) as HTMLElement | null;
      if (!status) return false;
      const text = (status.textContent || "").trim();
      if (!text) return false;

      const cs = getComputedStyle(toastEl);
      const padL = parseFloat(cs.paddingLeft) || 0;
      const padR = parseFloat(cs.paddingRight) || 0;
      const gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      // max-width ya resuelto a px por el browser
      const maxW =
        parseFloat(cs.maxWidth) ||
        (toastEl.parentElement
          ? toastEl.parentElement.getBoundingClientRect().width
          : window.innerWidth);
      if (maxW <= 0) return false;

      // El icono es el hijo inmediatamente anterior al mensaje. Si el toast no
      // trae icono (toast plano), el mensaje es el primer hijo → icono = 0.
      const children = Array.from(toastEl.children) as HTMLElement[];
      const statusIndex = children.indexOf(status);
      const iconW = statusIndex > 0 ? children[statusIndex - 1].offsetWidth : 0;

      // Botón de cierre (X) — último hijo cuando el toast no es loading
      const closeEl = toastEl.lastElementChild as HTMLElement | null;
      const isClose = closeEl?.classList.contains("app-toast-close") ?? false;
      const closeStyle = isClose && closeEl ? getComputedStyle(closeEl) : null;
      const closeW =
        isClose && closeEl
          ? closeEl.offsetWidth +
            (parseFloat(closeStyle?.marginLeft ?? "0") || 0)
          : 0;

      // Gaps reales entre hijos: (n hijos - 1) * gap
      const gaps = Math.max(0, toastEl.children.length - 1) * gap;

      const maxLine = widestTextLine(status, currentScale(toastEl));
      if (maxLine <= 0) return false;

      const chrome = iconW + closeW + gaps + padL + padR;
      const target = Math.min(maxW, Math.ceil(maxLine + chrome + 1));
      const currentW = toastEl.offsetWidth;
      if (target < currentW - 1) {
        // setProperty con !important para ganar al width:fit-content de index.css
        toastEl.style.setProperty("width", `${target}px`, "important");
        return true;
      }
      return false;
    };

    /**
     * Ajuste convergente de un toast. Empieza desde su ancho natural (quita
     * cualquier width previo para poder crecer si el mensaje cambió) y encoge
     * en pasadas hasta que no quede espacio sobrante. Todo ocurre en el mismo
     * frame, antes del siguiente paint, así que no hay parpadeo visible.
     */
    const settle = (toastEl: HTMLElement) => {
      toastEl.style.removeProperty("width");
      for (let i = 0; i < MAX_FIT_PASSES; i++) {
        if (!fitOnce(toastEl)) break;
      }
    };

    const schedule = (toastEl: HTMLElement) => {
      if (pend.has(toastEl)) return;
      pend.add(toastEl);
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          const batch = Array.from(pend);
          pend.clear();
          batch.forEach(settle);
        });
      }
    };

    const fromMutation = (node: Node) => {
      const el = node instanceof Element ? node : node.parentElement;
      if (!el) return;
      const toastEl = el.classList.contains("app-toast")
        ? (el as HTMLElement)
        : (el.closest(".app-toast") as HTMLElement | null);
      if (toastEl) schedule(toastEl);
    };

    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // Nuevos toasts (o contenido añadido dentro de uno ya visible)
        m.addedNodes.forEach(fromMutation);
        // Cambios de texto en un toast existente (mismo id, mensaje distinto)
        if (m.addedNodes.length === 0) fromMutation(m.target);
      }
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    document.querySelectorAll<HTMLElement>(".app-toast").forEach(schedule);

    // Re-ajusta al terminar de cargar las fuentes (Inter vía Google Fonts):
    // un swap tardío de tipografía no debe dejar ancho sobrante.
    if (document.fonts?.ready) {
      document.fonts.ready
        .then(() =>
          document
            .querySelectorAll<HTMLElement>(".app-toast")
            .forEach(schedule),
        )
        .catch(() => undefined);
    }

    // Re-ajusta al rotar/resize del viewport (cambia max-width disponible)
    const onResize = () => {
      document.querySelectorAll<HTMLElement>(".app-toast").forEach(schedule);
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });

    return () => {
      obs.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return null;
}
